import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./SubscriptionPlanUsage.module.scss";
import { UpdatePlanButton } from "src/features";
import { ProgressBar, Tooltip, Translation } from "src/components";
import {
  selectCompany,
  selectTrackersLimit,
  selectCompanyTrackers,
  selectCompanySubscriptionPlan,
  selectCompanyTrackersTotalCount,
} from "src/store/selectors";

export const SubscriptionPlanUsage: FC = () => {
  const { t } = useTranslation();

  const trackers = useSelector(selectCompanyTrackers);

  const { stripeCustomerId, stripeSubscriptionId, fortnoxCustomerId } =
    useSelector(selectCompany);

  const subscriptionPlan = useSelector(selectCompanySubscriptionPlan);

  const usedTrackers = useSelector(selectCompanyTrackersTotalCount);

  const totalTrackersCount = usedTrackers ?? trackers.length;

  const { trackersLimit } = useSelector(selectTrackersLimit);

  const trackersCountLabel = useMemo<string>(() => {
    if (!trackersLimit) return "";

    return t("page.settings.subscription_plan.label.trackers_limit", {
      count: totalTrackersCount,
      totalCount: trackersLimit,
    });
  }, [t, totalTrackersCount, trackersLimit]);

  const trackersPercentValue = useMemo<number>(() => {
    if (!totalTrackersCount) return 0;

    if (!trackersLimit) return 0;

    return totalTrackersCount / trackersLimit;
  }, [totalTrackersCount, trackersLimit]);

  const isTrackersLimitUpgradeAvailable = useMemo<boolean>(() => {
    if (!subscriptionPlan || !stripeCustomerId || !stripeSubscriptionId)
      return false;

    return (
      subscriptionPlan.category !== "free" &&
      subscriptionPlan.category !== "trial"
    );
  }, [stripeCustomerId, stripeSubscriptionId, subscriptionPlan]);

  const isInvoiceCustomer = useMemo<boolean>(
    () => Boolean(fortnoxCustomerId),
    [fortnoxCustomerId],
  );

  const updateButtonLabel = useMemo<string>(
    () =>
      isInvoiceCustomer
        ? t("page.settings.subscription_plan.button.contact_us")
        : t("page.settings.subscription_plan.button.update_trackers"),
    [isInvoiceCustomer, t],
  );

  if (!subscriptionPlan) return null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <span>{t("page.settings.subscription_plan.label.trackers")}</span>
            <span>
              {trackersCountLabel ||
                t("page.settings.subscription_plan.label.not_available")}
            </span>
          </div>
          <Tooltip
            content={
              <Translation
                i18nKey="page.settings.subscription_plan.label.trackers_statistic"
                values={{ trackersLimit, trackersCount: totalTrackersCount }}
              />
            }
          >
            <ProgressBar value={trackersPercentValue} />
          </Tooltip>
        </div>
      </div>
      {isTrackersLimitUpgradeAvailable && (
        <UpdatePlanButton className={styles.button} buttonStyle="outlined">
          <span>{updateButtonLabel}</span>
        </UpdatePlanButton>
      )}
    </div>
  );
};
